import _ from 'lodash';

import { CoreEnums } from '@/utils/domain/constants';
import BaseFieldHandler from '../BaseFieldHandler';
import { FIELD_SEARCH_TYPES, JSON_DATA_TYPES, REMOTE_DEFAULT_KEYS } from '../constants';
import {
  defaultFieldFilters,
  fieldParentsMap,
  getCurrentUser,
  instanceDataFieldValue,
  mapMultiChoice,
  mapMultiChoiceLabelValue,
  mapSingleChoiceLabelValue,
} from '../utils';

const { FormFieldType, FormFieldStringType, EntityQueryOperationType } = CoreEnums;

export default class SelectorFieldHandler extends BaseFieldHandler {
  static supportedTypes = [
    FormFieldStringType.SelectorUser,
    FormFieldStringType.SelectorPosition,
    FormFieldStringType.SelectorUserGroup,
    FormFieldStringType.SelectorArea,
    FormFieldStringType.WebServiceRemoteSearch,
    FormFieldStringType.WebServiceRemoteList,
    FormFieldStringType.FieldDynamicEntityList,
    FormFieldStringType.DataBaseRemoteList,
    FormFieldStringType.DataBaseRemoteSearch,
    FormFieldStringType.FieldDynamicEntitySearch,
    FormFieldType.SelectorUser,
    FormFieldType.SelectorPosition,
    FormFieldType.SelectorUserGroup,
    FormFieldType.SelectorArea,
    FormFieldType.WebServiceRemoteSearch,
    FormFieldType.WebServiceRemoteList,
    FormFieldType.FieldDynamicEntityList,
    FormFieldType.DataBaseRemoteList,
    FormFieldType.DataBaseRemoteSearch,
    FormFieldType.FieldDynamicEntitySearch,
  ];

  handle() {
    const config = this.getBaseConfig();
    const { isDetails, isLocked } = this.fieldState;
    const { isCustomList, isDynamicEntity, isContinue } = this.routeInfo;

    const {
      id,
      type,
      filters = [],
      allowMultiple,
      allowInsertNewOption,
      autoSelect,
      autoSelectAll,
      maxSelectionsAllowed,
      minSelectionsRequired,
      displayTextDynamicEntityField,
      userDisplayValue,
      userExtraFieldId,
      userExtraField,
      currentUser,
      operationType = EntityQueryOperationType.EntityForm,
      formId,
      // `entityId` may also be supplied via the field config itself,
      // not only inferred from the URL. CustomLists backed by an
      // entity (`dataOrigin: 2`) hydrate it into every field so the
      // backend's `field/getData` endpoint receives the required
      // parameter even though the page is `/custom-lists/view/...`
      // (which doesn't match the `dynamicEntity` route).
      entityId: configEntityId,
    } = this.baseConfig;

    const parents =
      !isLocked && !isDetails && this.dependency ? fieldParentsMap(filters, this.configData) : [];
    const deeps = this.getDependencies();
    const isObject = !allowMultiple && (_.isObject(config.value) || this.isJsonLike(config.value));
    const isRemoteSearch = FIELD_SEARCH_TYPES.includes(type);
    // Resolve `entityId` from the URL first (entity manager pages),
    // and fall back to the value injected by the upstream config
    // (e.g. CustomLists). When neither is present (e.g. process
    // forms) we don't add the prop at all.
    const resolvedEntityId = isDynamicEntity?.params?.id ?? configEntityId;

    const fieldTypeConfigs = this.getFieldTypeConfigs(
      type,
      id,
      displayTextDynamicEntityField,
      userDisplayValue,
      userExtraFieldId,
    );

    const isUserSelector =
      type === FormFieldStringType.SelectorUser || type === FormFieldType.SelectorUser;

    const fieldConfig = {
      ...config,
      // User selectors stay as select in details so UserPicture can render.
      type: isDetails && !allowMultiple && !isUserSelector ? 'text' : 'select',
      labelInValue: !this.dependency,

      ...(!isDetails && {
        locale: false,
        ...fieldTypeConfigs,
        ...REMOTE_DEFAULT_KEYS,
        httpMethod: 'post',
        apiRoute: `field/getData/${id}/${operationType}`,

        ...(isCustomList && { isCustomList: true }),
        ...(isLocked && { showSearch: false, allowClear: false }),
        ...(parents?.length > 0 && { parents, relationships: true }),
        ...(isRemoteSearch && { isRemoteSearch }),
        ...(allowInsertNewOption && { allowInsertOptionManually: allowInsertNewOption }),
        ...(resolvedEntityId && { isDynamicEntity: true, entityId: resolvedEntityId }),
        ...(JSON_DATA_TYPES.includes(type) && { jsonSelectData: true, labelInValue: true }),
        ...(filters?.length > 0 && defaultFieldFilters(filters, this.configData, formId)),
        ...(this.dependency && !isRemoteSearch && { isAutoSelectItemEnabled: autoSelect }),
        ...(deeps?.length > 0 && { deeps, labelInValue: true }),
        ...this.getCommonFormRouteProps(),
      }),

      // Details has no remote fetch, but still needs `user` so tags/value
      // use UserTagRender / UserPicture instead of plain ChoiceTagRender.
      ...(isDetails &&
        isUserSelector && {
          ...fieldTypeConfigs,
          labelInValue: true,
        }),

      ...(allowMultiple && {
        mode: 'multiple',
        // Cap chips for non-user multis. User selectors show every selected
        // person (wrap) — a "+ N" rest tag hides who was chosen.
        ...(!isDetails && !this.baseConfig.readOnly && !isUserSelector && { maxTagCount: 3 }),
        ...(!isRemoteSearch && { isAutoSelectAllEnabled: autoSelectAll }),
        // User multi needs `{ label, value: id }` in edit and details —
        // names-only (mapMultiChoice) breaks UserPicture photo loading.
        value: isUserSelector
          ? mapMultiChoiceLabelValue(config.value)
          : mapMultiChoice(config.value),
        ...(maxSelectionsAllowed > 1 && { maxSelectionsAllowed }),
        ...(minSelectionsRequired > 1 && { minSelectionsRequired }),
      }),

      ...(isObject && {
        value:
          isDetails && isUserSelector
            ? mapSingleChoiceLabelValue(config.value)
            : instanceDataFieldValue(
                JSON_DATA_TYPES.includes(type),
                config.value,
                this.dependency,
                isDetails,
              ),
      }),
    };

    if (isContinue && !isLocked && currentUser) {
      fieldConfig.value = getCurrentUser(type, { userDisplayValue, userExtraField });
    }

    return fieldConfig;
  }

  getFieldTypeConfigs(type, id, displayTextDynamicEntityField, userDisplayValue, userExtraFieldId) {
    const toEntityList = {
      dynamicEntityFieldId: id,
      displayConfig: displayTextDynamicEntityField,
    };

    const toSelUser = {
      itemDb: 'sourceUser.id',
      dynamicEntityFieldId: id,
      userDisplayValue,
      userExtraFieldId,
      user: true,
    };

    const configs = {
      [FormFieldStringType.SelectorArea]: { area: true },
      [FormFieldStringType.SelectorPosition]: { position: true },
      [FormFieldStringType.SelectorUserGroup]: { userGroup: true },
      [FormFieldStringType.FieldDynamicEntityList]: toEntityList,
      [FormFieldStringType.FieldDynamicEntitySearch]: toEntityList,
      [FormFieldStringType.SelectorUser]: toSelUser,
      [FormFieldType.SelectorArea]: { area: true },
      [FormFieldType.SelectorPosition]: { position: true },
      [FormFieldType.SelectorUserGroup]: { userGroup: true },
      [FormFieldType.FieldDynamicEntityList]: toEntityList,
      [FormFieldType.FieldDynamicEntitySearch]: toEntityList,
      [FormFieldType.SelectorUser]: toSelUser,
    };

    return configs[type] || {};
  }
}
